import { useMemo, useRef } from 'react'
import { AdminHeader } from '@components/layout/admin-header'
import { useTaxonomyTranslations } from '@global/hooks/queries/taxonomy-translations'
import {
  List,
  TaxonomyTranslationHeader,
  FeedbackModal,
  Pagination,
  SearchField,
  Sort,
} from '@taxonomy-translation/components'
import { usePagination } from '@taxonomy-translation/components/pagination/usePagination'
import { useSearch } from '@taxonomy-translation/components/search-field/useSearch'
import { useSort } from '@taxonomy-translation/components/sort/useSort'
import {
  TaxonomyTranslationPaginationParams,
  TaxonomyTranslationPaginationResponse,
} from '@taxonomy-translation/types'
import { ModalInterface } from '@components'
import { SortDirection } from '@global/api/types'
import { t } from '@lingui/core/macro'

const emptyResponse: TaxonomyTranslationPaginationResponse = {
  metadata: {
    limit: 0,
    totalItems: 0,
  },
  data: [],
}

export const TaxonomyTranslations = () => {
  const SORT_OPTIONS = [
    { label: t`Most recent`, value: SortDirection.DESC },
    { label: t`Oldest first`, value: SortDirection.ASC },
  ]

  const feedbackModalRef = useRef<ModalInterface>(null)
  const { cursor, page, onNextPage, onPreviousPage, isPreviousAvailable } = usePagination()
  const { sort, onSortChange } = useSort()
  const { searchTerm, debouncedSearchTerm: search, onSearchChange } = useSearch()

  const paginationParams = useMemo(
    (): TaxonomyTranslationPaginationParams => ({
      sort,
      cursor,
      search,
    }),
    [sort, cursor, search],
  )

  const { data = emptyResponse, isLoading } = useTaxonomyTranslations(paginationParams)
  const { data: translationList, metadata } = data

  const handleNextPage = () => {
    onNextPage(metadata?.next)
  }

  const handleOpenModal = () => {
    feedbackModalRef.current?.handleOpenModal()
  }

  return (
    <div className="flex flex-col grow h-full">
      <div>
        <AdminHeader>
          <TaxonomyTranslationHeader onFeedbackClick={handleOpenModal} />
        </AdminHeader>
      </div>
      <div className="flex flex-col height-full grow">
        <div className="px-8 py-16 flex flex-col grow">
          <div className="mb-6 flex flex-row h-9 items-baseline justify-between">
            <div className="w-[200px]">
              <SearchField onChange={onSearchChange} value={searchTerm} />
            </div>
            <Sort options={SORT_OPTIONS} value={sort} onChange={onSortChange} />
          </div>
          <List params={paginationParams} data={translationList} isLoading={isLoading} />
        </div>
        <div className="sticky bottom-0 right-0 bg-white border-t border-gray-200 px-8">
          <Pagination
            page={page}
            limit={metadata.limit}
            total={metadata.totalItems}
            isPreviousAvailable={isPreviousAvailable}
            nextCursor={metadata?.next}
            onNextClick={handleNextPage}
            onPreviousClick={onPreviousPage}
          />
        </div>
      </div>
      <FeedbackModal ref={feedbackModalRef} />
    </div>
  )
}
